import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppMediaQuery } from "./AppMediaQuery";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppContact } from "./AppIndex";
import { AppViews } from "./AppViews";
import { AppIcons } from "./AppIcons";
import { AppThemeRules } from "./AppTheme";
import { AppThemeRun, AppThemeStore } from "./AppThemeRun";
import { AppAskAllRules } from "./AppAskAllRules";
import { AppAssistView } from "./AppAssistView";
import { AppExportRules } from "./AppExportRules";
import { AppReactions } from "./AppReactions";
import { AppTaskFrame } from "./AppTaskFrame";
import { AppFrameA11y, IFrameA11yDoc } from "./AppFrameA11y";
import { AppChatLink, AppPaletteContext, AppPaletteItem, AppPaletteRecentStore, AppPaletteRules, AppPaletteView,
    IAppPalettePick } from "./AppPalette";

// AppKeys - keyboard shortcuts (owner-approved design item 10, 2026-09-30: "keyboard shortcuts - yes").
//   /            focus the search box            Ctrl/Cmd+K   the command palette (`AppPalette`)
//   Esc          close a menu / the AI sheet, or leave the field you are typing in
//   Alt+Up/Down  the previous / next chat in the chat list's order (opens it - navigation only)
//   g c / g t / g o   Chats, Tasks, Overview (the second key within 1 s)
//   a            the AI panel on a contact page          ?   this list (`HelpHtml`)
//
// ⛔ DECISION 9: NOTHING HERE SENDS OR QUEUES A MESSAGE. Every action navigates, focuses, opens or closes UI, or starts the
// download of the owner's own chat. `AppKeys.test.ts` greps this file and `AppPalette.ts` for every sender name.
// ⛔ TYPING WINS: while the focus is in an input, a textarea, a select or an editable element only Esc and Ctrl/Cmd+K act.
// ⛔ THE BROWSER'S OWN COMBINATIONS ARE LEFT ALONE: a plain key acts only with no Ctrl, Cmd or Alt; Ctrl+Alt (AltGr on
// many keyboards) never acts; an IME composition never acts.
//
// ONE keydown listener on the document (plus the same listener on the contact frame's document - a key pressed inside
// the `blob:` frame never reaches the parent). Split: `AppKeyRules` (pure: which key means what, the list order, the help
// text), `AppKeyDispatch` (what an action does, through `IAppKeyHost` - no DOM, so it is tested), `AppKeyHost` (the page),
// `AppKeyHelpView` (the help <dialog>), `AppKeys` (the listener).

/** One key press, as the rules need it. */
export class AppKeyPress {
    public Key: string = "";
    public Ctrl: boolean = false;
    public Meta: boolean = false;
    public Alt: boolean = false;
    public Shift: boolean = false;
    public InField: boolean = false;
    public Composing: boolean = false;
    /** Pressed inside the contact's frame (its own document), not the app page. */
    public InFrame: boolean = false;
    /** The browser's auto-repeat of a held key (`KeyboardEvent.repeat`). */
    public Repeat: boolean = false;
}

/** What `AppMain.KeyFacts` hands over - facts, never a sender. */
export class AppKeyFacts {
    public Loaded: boolean = false;
    public Contacts: AppContact[] = [];
    /** The chat list's search text (the list order is filtered by it). */
    public Filter: string = "";
    /** "Ask about everyone" is offered on this page (the owner, with a service). */
    public AskAll: boolean = false;
    /** The Sent tab exists. */
    public Sent: boolean = false;
}

export class AppKeyRules {
    public static readonly SequenceMs: number = 1000;

    public static readonly None: string = "";
    public static readonly Search: string = "search";
    public static readonly Palette: string = "palette";
    public static readonly Escape: string = "escape";
    public static readonly Prev: string = "prev";
    public static readonly Next: string = "next";
    public static readonly Chats: string = "chats";
    public static readonly Tasks: string = "tasks";
    public static readonly Overview: string = "overview";
    public static readonly Assist: string = "assist";
    public static readonly Help: string = "help";
    public static readonly Arm: string = "arm";

    /** The action a press means. `PendingAt` = when a lone `g` was pressed (-1 none). */
    public static Decide = (P: AppKeyPress, PendingAt: number, Now: number): string => {
        if (getCoreUtils().KErrorMode === true) { return AppKeyRules.None; }
        try {
            if (P.Composing === true || (P.Ctrl === true && P.Alt === true)) { return AppKeyRules.None; }
            const Key: string = P.Key;
            if ((P.Ctrl === true || P.Meta === true) && P.Alt === false && P.Shift === false && (Key === "k" || Key === "K")) {
                return AppKeyRules.Palette;
            }
            if (Key === "Escape" && P.Ctrl === false && P.Meta === false && P.Alt === false && P.Repeat === false) { return AppKeyRules.Escape; }
            if (P.InField === true) { return AppKeyRules.None; }
            if (P.Alt === true) { return AppKeyRules.AltKey(P); }
            // ⛔ A HELD KEY IS ONE PRESS: holding `a` must not flick the AI panel open and shut, `?` / `/` / `g` act once, and
            // a held Esc peels one layer, not all of them. Only Alt+Up/Down repeats (above) - holding it walks the list,
            // like holding an arrow key, and each step is a plain route change.
            if (P.Repeat === true) { return AppKeyRules.None; }
            if (P.Ctrl === true || P.Meta === true) { return AppKeyRules.None; }
            if (PendingAt >= 0 && Now - PendingAt <= AppKeyRules.SequenceMs && P.Shift === false) { return AppKeyRules.Second(Key); }
            if (Key === "?") { return AppKeyRules.Help; }
            if (Key === "/") { return AppKeyRules.Search; }
            if (P.Shift === true) { return AppKeyRules.None; }
            if (Key === "g") { return AppKeyRules.Arm; }
            if (Key === "a") { return AppKeyRules.Assist; }
            return AppKeyRules.None;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Decide");
            return AppKeyRules.None;
        }
    };

    private static AltKey = (P: AppKeyPress): string => {
        if (getCoreUtils().KErrorMode === true) { return AppKeyRules.None; }
        try {
            if (P.Meta === true || P.Shift === true) { return AppKeyRules.None; }
            if (P.Key === "ArrowUp") { return AppKeyRules.Prev; }
            if (P.Key === "ArrowDown") { return AppKeyRules.Next; }
            return AppKeyRules.None;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.AltKey");
            return AppKeyRules.None;
        }
    };

    /** The key after `g`. Anything else ends the sequence and does nothing. */
    private static Second = (Key: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppKeyRules.None; }
        try {
            if (Key === "c") { return AppKeyRules.Chats; }
            if (Key === "t") { return AppKeyRules.Tasks; }
            if (Key === "o") { return AppKeyRules.Overview; }
            return AppKeyRules.None;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Second");
            return AppKeyRules.None;
        }
    };

    /** Is this element one the owner types into? (Tag names as strings: the frame's elements are another realm.) */
    public static IsField = (Tag: string, Type: string, Editable: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Editable === true) { return true; }
            const T: string = nz(Tag).toUpperCase();
            if (T === "TEXTAREA" || T === "SELECT") { return true; }
            if (T !== "INPUT") { return false; }
            const Kind: string = nz(Type).toLowerCase();
            return ["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"].indexOf(Kind) < 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.IsField");
            return false;
        }
    };

    /** The chat links in the chat list's order: the search filter applied, newest first - what the cards show. */
    public static Order = (Contacts: AppContact[], Filter: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            for (const One of AppViews.NewestFirst(AppViews.Filtered(Contacts, Filter))) { Out.push(AppChatLink.Of(One)); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Order");
            return [];
        }
    };

    /** The chat one step away (`Dir` +1 next / -1 previous), no wrap: "" past either end. Not on a chat = the first. */
    public static Step = (Links: string[], Current: string, Dir: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Links.length === 0) { return ""; }
            const At: number = Links.indexOf(Current);
            if (At < 0) { return Links[0]; }
            const To: number = At + Dir;
            if (To < 0 || To >= Links.length) { return ""; }
            return Links[To];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Step");
            return "";
        }
    };

    /** A Mac by its platform name (`navigator.userAgentData.platform`, else `navigator.platform`). */
    public static IsMac = (Platform: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return /mac|iphone|ipad|ipod/i.test(nz(Platform));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.IsMac");
            return false;
        }
    };

    /** Every shortcut as [keys, what it does]; a key "then" marks a sequence, "up"/"down" the arrows. */
    public static Shortcuts = (Mac: boolean): string[][] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            let Mod: string = "Ctrl";
            let Alt: string = "Alt";
            if (Mac === true) { Mod = "⌘"; Alt = "⌥"; }
            return [
                ["/", "Search your chats"],
                [Mod + " K", "Go to a chat, a view or a command"],
                [Alt + " up", "Open the chat above in the list"],
                [Alt + " down", "Open the chat below in the list"],
                ["g then c", "Go to Chats"],
                ["g then t", "Go to Tasks"],
                ["g then o", "Go to Overview"],
                ["a", "Open or close the AI panel on a chat"],
                ["Tab", "In a conversation, focus a message or its actions"],
                ["up down", "On a message, move to the previous or next message"],
                ["Home End", "On a message, go to the first (Home) or last (End) message"],
                ["Enter", "On a message, show reaction and task actions"],
                ["r", "On a message, open its reactions - left and right move, Enter picks"],
                ["Shift F10", "On a message, open its reactions (the Menu key does too)"],
                ["Esc", "Close a menu or panel, or leave the box you are typing in; from message actions, return to the message"],
                ["?", "Show these shortcuts"],
            ];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Shortcuts");
            return [];
        }
    };

    /** The help dialog's inside: a heading, the close button, the list. Constant text of ours, escaped anyway. */
    public static HelpHtml = (Mac: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Mod: string = "Ctrl K";
            if (Mac === true) { Mod = "⌘ K"; }
            const Out: string[] = ["<div class=\"khead\"><h2 id=\"" + AppKeyHelpView.TitleId + "\">Keyboard shortcuts</h2>"
                + "<button type=\"button\" class=\"btn ghost icon sm\" data-wac-keys-close aria-label=\"Close\">"
                + AppIcons.Svg(AppIcons.Close, 16) + "</button></div><dl class=\"keylist\">"];
            for (const Row of AppKeyRules.Shortcuts(Mac)) {
                Out.push("<div class=\"krow\"><dt>" + AppKeyRules.Keys(Row[0]) + "</dt><dd>" + kHTMLCore.EscapeHtml(Row[1]) + "</dd></div>");
            }
            Out.push("</dl><p class=\"knote\">Shortcuts wait while you type in a box - except Esc and " + kHTMLCore.EscapeHtml(Mod) + ".</p>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.HelpHtml");
            return "";
        }
    };

    /** "Alt up" -> <kbd>Alt</kbd><kbd>(chevron)</kbd>; "g then c" -> <kbd>g</kbd> then <kbd>c</kbd>. No typed arrows. */
    private static Keys = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = [];
            for (const Word of Text.split(" ")) {
                if (Word === "then") { Out.push("<span class=\"kthen\">then</span>"); continue; }
                if (Word === "up") { Out.push(AppIcons.ArrowKey(true)); continue; }
                if (Word === "down") { Out.push(AppIcons.ArrowKey(false)); continue; }
                Out.push("<kbd>" + kHTMLCore.EscapeHtml(Word) + "</kbd>");
            }
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyRules.Keys");
            return "";
        }
    };
}

/** What an action may touch on the page. ⛔ No member sends or queues anything (decision 9). */
export interface IAppKeyHost {
    Facts(): AppKeyFacts;
    Hash(): string;
    Go(Hash: string): void;
    FocusSearch(): boolean;
    CanAssist(): boolean;
    ToggleAssist(): void;
    OpenAssist(): void;
    CloseMenus(): boolean;
    /** The contact frame shows the reaction row - its own Esc listener (`AppReactions`) closes it. */
    FrameMenuOpen(): boolean;
    /** Closes the frame's reaction row through its own Esc path (for an Esc pressed on the app page). */
    CloseFrameMenu(): boolean;
    CloseAssistSheet(): boolean;
    BlurField(): boolean;
    CanExport(): boolean;
    Export(): void;
    AskAll(): void;
    Theme(Choice: string): void;
    RecentRead(): string[];
    RecentWrite(List: string[]): void;
}

/** The palette and the help dialog, as the dispatcher sees them. */
export interface IAppKeyViews {
    PaletteOpen(): boolean;
    OpenPalette(Ctx: AppPaletteContext): void;
    HelpOpen(): boolean;
    OpenHelp(): void;
    CloseHelp(): void;
}

/** What each action does. No DOM - `AppKeys.test.ts` drives it with a recording host. */
export class AppKeyDispatch implements IAppPalettePick {
    public PendingAt: number = -1;
    private Host: IAppKeyHost;
    private Views: IAppKeyViews;

    public constructor(Host: IAppKeyHost, Views: IAppKeyViews) {
        this.Host = Host;
        this.Views = Views;
    }

    /** One press. True = it was ours (the page's default is then prevented). */
    public Press = (P: AppKeyPress, Now: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Views.PaletteOpen() === true) { return false; }
            if (this.Views.HelpOpen() === true) {
                if (P.Key === "Escape") { this.Views.CloseHelp(); return true; }
                return false;
            }
            const Act: string = AppKeyRules.Decide(P, this.PendingAt, Now);
            this.PendingAt = -1;
            if (Act === AppKeyRules.Arm) { this.PendingAt = Now; return true; }
            return this.Do(Act, P.InFrame);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Press");
            return false;
        }
    };

    private Do = (Act: string, InFrame: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Act === AppKeyRules.Search) { return this.Host.FocusSearch(); }
            if (Act === AppKeyRules.Palette) { this.Views.OpenPalette(this.Context()); return true; }
            if (Act === AppKeyRules.Escape) { return this.Escape(InFrame); }
            if (Act === AppKeyRules.Prev) { return this.Step(-1); }
            if (Act === AppKeyRules.Next) { return this.Step(1); }
            if (Act === AppKeyRules.Chats || Act === AppKeyRules.Tasks || Act === AppKeyRules.Overview) { return this.View(Act); }
            if (Act === AppKeyRules.Assist) {
                if (this.Host.CanAssist() === false) { return false; }
                this.Host.ToggleAssist();
                return true;
            }
            if (Act === AppKeyRules.Help) { this.Views.OpenHelp(); return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Do");
            return false;
        }
    };

    /**
     * Esc: a menu first, then the AI sheet, then the field being typed in. ⛔ An Esc pressed in the frame while its reaction
     * row is open is the row's (its own listener on the frame closes it) - closing the AI sheet too would take two things
     * down with one key (verifier, 2026-09-30).
     */
    private Escape = (InFrame: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Host.FrameMenuOpen() === true) {
                if (InFrame === true) { return false; }
                return this.Host.CloseFrameMenu();
            }
            if (this.Host.CloseMenus() === true) { return true; }
            if (this.Host.CloseAssistSheet() === true) { return true; }
            return this.Host.BlurField();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Escape");
            return false;
        }
    };

    private Step = (Dir: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Facts: AppKeyFacts = this.Host.Facts();
            if (Facts.Loaded === false) { return false; }
            const Links: string[] = AppKeyRules.Order(Facts.Contacts, Facts.Filter);
            const Target: string = AppKeyRules.Step(Links, AppChatLink.CurrentOf(this.Host.Hash()), Dir);
            if (Target.length === 0) { return false; }
            this.Host.Go(Target);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Step");
            return false;
        }
    };

    private View = (Act: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Host.Facts().Loaded === false) { return false; }
            this.Host.Go("#" + Act);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.View");
            return false;
        }
    };

    /** What the palette may offer on this page, now. */
    public Context = (): AppPaletteContext => {
        if (getCoreUtils().KErrorMode === true) { return new AppPaletteContext(); }
        try {
            const Facts: AppKeyFacts = this.Host.Facts();
            const Out: AppPaletteContext = new AppPaletteContext();
            Out.Loaded = Facts.Loaded;
            Out.Contacts = Facts.Contacts;
            Out.Sent = Facts.Sent;
            Out.AskAll = Facts.AskAll;
            Out.CanAssist = this.Host.CanAssist();
            Out.CanExport = this.Host.CanExport();
            Out.Recent = this.Host.RecentRead();
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Context");
            return new AppPaletteContext();
        }
    };

    /** A palette row was chosen: a chat or a view is a route; an action opens UI. ⛔ Nothing sends (decision 9). */
    public Pick = (Item: AppPaletteItem): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Item.Link.length > 0) { this.Host.Go(Item.Link); return; }
            const Act: string = Item.Act;
            if (Act === AppPaletteRules.ActAssist) { this.Host.OpenAssist(); return; }
            if (Act === AppPaletteRules.ActAskAll) { this.Host.AskAll(); return; }
            if (Act === AppPaletteRules.ActExport) { this.Host.Export(); return; }
            if (Act === AppPaletteRules.ActThemeLight) { this.Host.Theme(AppThemeRules.Light); return; }
            if (Act === AppPaletteRules.ActThemeDark) { this.Host.Theme(AppThemeRules.Dark); return; }
            if (Act === AppPaletteRules.ActThemeSystem) { this.Host.Theme(AppThemeRules.System); return; }
            if (Act === AppPaletteRules.ActHelp) { this.Views.OpenHelp(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Pick");
        }
    };

    /** The address changed: a chat page opened (by a card, the palette or a key) goes to the top of the recent list. */
    public Opened = (Hash: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Href: string = AppChatLink.HrefOf(Hash);
            if (Href.length === 0) { return; }
            this.Host.RecentWrite(AppPaletteRules.Remember(this.Host.RecentRead(), Href));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyDispatch.Opened");
        }
    };
}

/** Where `AppKeyHost` reads the app's facts - `AppMain.KeyFacts`. */
export interface IAppKeyFactsSource {
    KeyFacts(): AppKeyFacts;
}

/**
 * The page, for the dispatcher. Everything goes through the elements the app already draws - the search box, the AI
 * panel's own open/close buttons, the export button, the "Ask about everyone" button - so a shortcut does exactly what
 * a click does. When the element is on another view, the route is taken first and the step runs after the repaint.
 */
export class AppKeyHost implements IAppKeyHost {
    /** Where the AI panel is a column, not a sheet - equal to `AppMain.AssistWideQuery` (pinned by a test). */
    public static readonly WideQuery: string = "(min-width: 1100px)";
    private static readonly AfterSearch: string = "search";
    private static readonly AfterAskAll: string = "askall";

    private Source: IAppKeyFactsSource;
    private Pending: string = "";

    public constructor(Source: IAppKeyFactsSource) {
        this.Source = Source;
    }

    public Facts = (): AppKeyFacts => {
        if (getCoreUtils().KErrorMode === true) { return new AppKeyFacts(); }
        try {
            return this.Source.KeyFacts();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.Facts");
            return new AppKeyFacts();
        }
    };

    public Hash = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return nz(window.location.hash);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.Hash");
            return "";
        }
    };

    public Go = (Hash: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (window.location.hash === Hash) { this.AfterRoute(); return; }
            window.location.hash = Hash;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.Go");
        }
    };

    public FocusSearch = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Box: HTMLInputElement | null = document.getElementById(AppViews.FindId) as HTMLInputElement | null;
            if (Box !== null) { Box.focus(); Box.select(); return true; }
            if (this.Facts().Loaded === false) { return false; }
            this.Pending = AppKeyHost.AfterSearch;
            this.Go("#chats");
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.FocusSearch");
            return false;
        }
    };

    public CanAssist = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return document.getElementById(AppAssistView.OpenId) !== null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CanAssist");
            return false;
        }
    };

    /** The panel's own ✦ button, clicked - so the app's own open/close/remember rules run. */
    public ToggleAssist = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Button: HTMLElement | null = document.getElementById(AppAssistView.OpenId);
            if (Button !== null) { Button.click(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.ToggleAssist");
        }
    };

    public OpenAssist = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.ClickOpenButton(AppAssistView.OpenId);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.OpenAssist");
        }
    };

    /**
     * An open task "…" menu - on the Tasks tab (`details.tmenu[open]`) or in the contact frame's task strip
     * (`.wac-tfctl details[open]`, `AppTaskFrame`) - closed, and focus goes back to its summary.
     */
    public CloseMenus = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            let Open: Element[] = Array.from(document.querySelectorAll("details.tmenu[open]"));
            const Doc: Document | null = AppKeyHost.FrameDoc();
            if (Doc !== null) { Open = Open.concat(Array.from(Doc.querySelectorAll("." + AppTaskFrame.ControlsClass + " details[open]"))); }
            if (Open.length === 0) { return AppKeyHost.CloseOtherLayer(Doc); }
            for (const One of Open) {
                One.removeAttribute("open");
                const Summary: HTMLElement | null = One.querySelector("summary");
                if (Summary !== null) { Summary.focus(); }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CloseMenus");
            return false;
        }
    };

    /**
     * ⭐ THE OTHER LAYERS Esc CLOSES, ONE AT A TIME (audit 2026-10-01 - each stayed open under Esc): the contact page's Insights drawer and Details sheet (in the frame). The focus goes back to what opened the
     * layer. ⛔ Closing only - nothing is saved, added or sent by it.
     * ⚠ The translation settings are no longer a layer of the page (2026-10-13): they are in the "Chat settings" dialog,
     * a native modal that closes itself on Esc - and `OnKeyDown` leaves every key pressed in such a dialog alone.
     */
    private static CloseOtherLayer = (Doc: Document | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Doc !== null && AppFrameA11y.CloseDrawer(Doc as unknown as IFrameA11yDoc) === true) { return true; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CloseOtherLayer");
            return false;
        }
    };

    public FrameMenuOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Doc: Document | null = AppKeyHost.FrameDoc();
            return Doc !== null && Doc.querySelector("." + AppReactions.RowClass) !== null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.FrameMenuOpen");
            return false;
        }
    };

    /**
     * An Esc pressed on the app page while the frame's reaction row is open: the row is closed by handing its own
     * listener (`AppReactions.OnKey`, on the frame document) an Escape - one layer, and no second closing rule here.
     */
    public CloseFrameMenu = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Doc: Document | null = AppKeyHost.FrameDoc();
            if (Doc === null || Doc.defaultView === null || Doc.querySelector("." + AppReactions.RowClass) === null) { return false; }
            const View: Window & typeof globalThis = Doc.defaultView as Window & typeof globalThis;
            Doc.dispatchEvent(new View.KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CloseFrameMenu");
            return false;
        }
    };

    /** The open contact frame's document, or null (no contact open, or not readable). */
    private static FrameDoc = (): Document | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Frame: HTMLIFrameElement | null = document.getElementById("wac-frame") as HTMLIFrameElement | null;
            if (Frame === null) { return null; }
            return Frame.contentDocument;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a frame we may not read has no menus we could close.
            return null;
        }
    };

    /** The AI panel when it is a SHEET (narrow window) - closed with its own × button. A column stays. */
    public CloseAssistSheet = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Card: HTMLElement | null = document.getElementById("wac-contact");
            if (Card === null || Card.classList.contains("ai-on") === false) { return false; }
            if (AppMediaQuery.Matches(AppKeyHost.WideQuery) === true) { return false; }
            const Close: HTMLElement | null = document.getElementById(AppAssistView.CloseId);
            if (Close === null) { return false; }
            Close.click();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CloseAssistSheet");
            return false;
        }
    };

    public BlurField = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Active: HTMLElement | null = document.activeElement as HTMLElement | null;
            if (Active === null || Active === document.body) { return false; }
            const Type: string = nz(Active.getAttribute("type"));
            if (AppKeyRules.IsField(Active.tagName, Type, Active.isContentEditable === true) === false) { return false; }
            Active.blur();
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.BlurField");
            return false;
        }
    };

    public CanExport = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Button: HTMLButtonElement | null = document.getElementById(AppExportRules.ButtonId) as HTMLButtonElement | null;
            return Button !== null && Button.disabled !== true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.CanExport");
            return false;
        }
    };

    /** The existing "Export chat (CSV)" button, clicked - a download of the owner's own chat. */
    public Export = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.CanExport() === false) { return; }
            const Button: HTMLElement | null = document.getElementById(AppExportRules.ButtonId);
            if (Button !== null) { Button.click(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.Export");
        }
    };

    /** "Ask about everyone" lives on the Chats view: opened there, the route taken first when needed. */
    public AskAll = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.OpenAskAll() === true) { return; }
            this.Pending = AppKeyHost.AfterAskAll;
            this.Go("#chats");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.AskAll");
        }
    };

    /** The open-button step `OpenAssist` and `OpenAskAll` share: found and clicked unless already expanded. */
    private ClickOpenButton = (Id: string): boolean => {
        const Button: HTMLElement | null = document.getElementById(Id);
        if (Button === null) { return false; }
        if (Button.getAttribute("aria-expanded") !== "true") { Button.click(); }
        return true;
    };

    private OpenAskAll = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.ClickOpenButton(AppAskAllRules.OpenId);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.OpenAskAll");
            return false;
        }
    };

    public Theme = (Choice: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppThemeStore.Write(Choice);
            AppThemeRun.Shared.Apply(Choice);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.Theme");
        }
    };

    public RecentRead = (): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            return AppPaletteRecentStore.Read();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.RecentRead");
            return [];
        }
    };

    public RecentWrite = (List: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppPaletteRecentStore.Write(List);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.RecentWrite");
        }
    };

    /** After the app repainted a route this host asked for: the step that needed that view's element. */
    public AfterRoute = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Want: string = this.Pending;
            this.Pending = "";
            if (Want === AppKeyHost.AfterSearch) {
                const Box: HTMLInputElement | null = document.getElementById(AppViews.FindId) as HTMLInputElement | null;
                if (Box !== null) { Box.focus(); }
            }
            if (Want === AppKeyHost.AfterAskAll) { this.OpenAskAll(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHost.AfterRoute");
        }
    };
}

/** The shortcuts help as a native modal <dialog>: the rest of the page is inert, Esc closes, focus returns. */
export class AppKeyHelpView {
    public static readonly Id: string = "wac-keyhelp";
    public static readonly TitleId: string = "wac-keyhelp-title";

    private Dialog: HTMLDialogElement | null = null;
    private Back: HTMLElement | null = null;

    public IsOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Dialog !== null && this.Dialog.open === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHelpView.IsOpen");
            return false;
        }
    };

    public Open = (Mac: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.IsOpen() === true) { return; }
            if (this.Dialog === null) {
                const Made: HTMLDialogElement = document.createElement("dialog");
                Made.id = AppKeyHelpView.Id;
                Made.className = "wac-keydlg";
                Made.setAttribute("aria-labelledby", AppKeyHelpView.TitleId);
                Made.innerHTML = AppKeyRules.HelpHtml(Mac);
                Made.addEventListener("click", this.OnClick);
                Made.addEventListener("close", this.OnClosed);
                document.body.appendChild(Made);
                this.Dialog = Made;
            }
            this.Back = document.activeElement as HTMLElement | null;
            if (typeof this.Dialog.showModal === "function") { this.Dialog.showModal(); } else { this.Dialog.setAttribute("open", ""); }
            const Close: HTMLElement | null = this.Dialog.querySelector("[data-wac-keys-close]");
            if (Close !== null) { Close.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHelpView.Open");
        }
    };

    public Close = (): void => {
        AppPaletteView.CloseShared(this.Dialog, this.IsOpen, this.OnClosed, "AppKeyHelpView.Close");
    };

    /** The close button, or the dim backdrop (the dialog element itself). */
    private OnClick = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === this.Dialog) { this.Close(); return; }
            if (Target !== null && typeof Target.closest === "function" && Target.closest("[data-wac-keys-close]") !== null) { this.Close(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHelpView.OnClick");
        }
    };

    private OnClosed = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Back !== null && this.Back.isConnected === true) { this.Back.focus(); }
            this.Back = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeyHelpView.OnClosed");
        }
    };
}

/**
 * The installed shortcuts: one document keydown listener (and the same one on each contact frame), the palette, the
 * help. `AppMain.Start` calls `AppKeys.Install(new AppKeyHost(this))` once. `[data-wac-keys-help]` on any element (the
 * top bar's "?") opens the help by one delegated click listener - or call `AppKeys.OpenHelp()`.
 */
export class AppKeys implements IAppKeyViews, IAppPalettePick {
    private static Installed: AppKeys | null = null;

    private Dispatch: AppKeyDispatch;
    private Host: AppKeyHost;
    private Palette: AppPaletteView;
    private Help: AppKeyHelpView = new AppKeyHelpView();
    private Frames: WeakSet<Document> = new WeakSet<Document>();

    private constructor(Host: AppKeyHost) {
        this.Host = Host;
        this.Palette = new AppPaletteView(this);
        this.Dispatch = new AppKeyDispatch(Host, this);
    }

    public static Install = (Host: AppKeyHost): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppKeys.Installed !== null) { return; }
            const Made: AppKeys = new AppKeys(Host);
            AppKeys.Installed = Made;
            document.addEventListener("keydown", Made.OnKeyDown);
            document.addEventListener("load", Made.OnLoadCapture, true);
            document.addEventListener("click", Made.OnClick);
            window.addEventListener("hashchange", Made.OnHashChange);
            Made.Dispatch.Opened(window.location.hash);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.Install");
        }
    };

    /** The help dialog's inside, for a caller that draws its own (e.g. a top-bar popover). */
    public static HelpHtml = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppKeyRules.HelpHtml(AppKeys.Mac());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.HelpHtml");
            return "";
        }
    };

    /** Opens the shortcuts dialog (the top bar's "?" button). */
    public static OpenHelp = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppKeys.Installed !== null) { AppKeys.Installed.OpenHelp(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OpenHelp");
        }
    };

    private static Mac = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Data: { platform?: string } | undefined = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
            let Platform: string = nz(navigator.platform);
            if (Data !== undefined && Data !== null && typeof Data.platform === "string" && Data.platform.length > 0) { Platform = Data.platform; }
            return AppKeyRules.IsMac(Platform);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable platform only decides whether the help says Ctrl or ⌘.
            return false;
        }
    };

    private OnKeyDown = (Ev: KeyboardEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Ev.defaultPrevented === true) { return; }
            // ⛔ A KEY PRESSED IN ANOTHER MODAL DIALOG IS THAT DIALOG'S ("Chat settings", a message in its conversation):
            // its Esc closes IT and nothing under it (the AI sheet, a menu), and "g c" or "a" typed on one of its buttons
            // must not move the page behind a modal. The palette and the help are this file's own and are asked first.
            if (this.PaletteOpen() === false && this.HelpOpen() === false && AppKeys.InModal(Ev.target) === true) { return; }
            const P: AppKeyPress = new AppKeyPress();
            P.Key = nz(Ev.key);
            P.Ctrl = Ev.ctrlKey === true;
            P.Meta = Ev.metaKey === true;
            P.Alt = Ev.altKey === true;
            P.Shift = Ev.shiftKey === true;
            P.Composing = Ev.isComposing === true;
            P.InField = AppKeys.InField(Ev.target);
            P.InFrame = Ev.currentTarget !== document;
            P.Repeat = Ev.repeat === true;
            if (this.Dispatch.Press(P, Date.now()) === true) { Ev.preventDefault(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OnKeyDown");
        }
    };

    /** Is the key's target inside an open `<dialog>`? */
    private static InModal = (Target: EventTarget | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const El: Element | null = Target as Element | null;
            if (El === null || typeof El.closest !== "function") { return false; }
            return El.closest("dialog[open]") !== null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.InModal");
            return false;
        }
    };

    private static InField = (Target: EventTarget | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const El: Element | null = Target as Element | null;
            if (El === null || typeof El.getAttribute !== "function") { return false; }
            const Editable: boolean = (El as HTMLElement).isContentEditable === true;
            return AppKeyRules.IsField(nz(El.tagName), nz(El.getAttribute("type")), Editable);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.InField");
            return false;
        }
    };

    /** A key pressed inside the contact's `blob:` frame never reaches this document - the frame gets the same listener. */
    private OnLoadCapture = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === null || nz(Target.tagName).toUpperCase() !== "IFRAME") { return; }
            const Doc: Document | null = (Target as HTMLIFrameElement).contentDocument;
            if (Doc === null || this.Frames.has(Doc) === true) { return; }
            this.Frames.add(Doc);
            Doc.addEventListener("keydown", this.OnKeyDown);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a frame we may not read (another origin) simply has no shortcuts inside it.
            return;
        }
    };

    private OnClick = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            if (Target.closest("[data-wac-keys-help]") === null) { return; }
            Ev.preventDefault();
            this.OpenHelp();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OnClick");
        }
    };

    /** Registered after `AppMain`'s own, so the app has repainted the route by now. */
    private OnHashChange = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Dispatch.Opened(window.location.hash);
            this.Host.AfterRoute();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OnHashChange");
        }
    };

    public PaletteOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Palette.IsOpen();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.PaletteOpen");
            return false;
        }
    };

    public OpenPalette = (Ctx: AppPaletteContext): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Help.Close();
            this.Palette.Open(Ctx);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OpenPalette");
        }
    };

    public HelpOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.Help.IsOpen();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.HelpOpen");
            return false;
        }
    };

    public OpenHelp = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Palette.Close();
            this.Help.Open(AppKeys.Mac());
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.OpenHelp");
        }
    };

    public CloseHelp = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Help.Close();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.CloseHelp");
        }
    };

    public Pick = (Item: AppPaletteItem): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Dispatch.Pick(Item);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKeys.Pick");
        }
    };
}
